import { FederatedPointerEvent, Graphics, IDestroyOptions, Sprite, Text, TextStyle, Texture, Ticker } from "pixi.js";
import { CommonResourcesPackage } from "../resources/CommonResourcesPackage";
import { GameFont } from "../GameFont";
import { Event } from "event-system";
import { UIButtonBase } from "./UIButtonBase";

export class AdMultiplierComponent extends Sprite {
    public readonly timeout: Event<void>;
    private readonly timerBackground: Sprite;
    private readonly rewardBackground: Sprite;
    private readonly reward: Sprite;
    private readonly progressCircle: Sprite;
    private readonly button: UIButtonBase;
    private readonly progressMask: Graphics;
    private boxMultiplierTimeout: number;
    protected cancelled: boolean = false;

    public set value(value: number) {
        this.progressValue = Math.max(Math.min(value, 1), 0);
        this.onProgress();
        if (this.progressValue == 0) {
            this.button.defaultView.tint = "#9c9c9c";
        }
    }

    public get value(): number {
        return this.progressValue;
    }

    public get onAdClick(): Event<UIButtonBase, FederatedPointerEvent> {
        return this.button.onUp;
    }

    public constructor(
        commonResources: CommonResourcesPackage,
        value: Texture | string,
        initialVisible: boolean = false,
        private progressValue: number = 1,
        private readonly timeoutMS: number = 10000

    ) {
        super(commonResources.assets_common_multiplier_ad_substrate_png);

        this.timerBackground = new Sprite(commonResources.assets_common_multiplier_ad_timer_background_png);
        this.rewardBackground = new Sprite(commonResources.assets_common_multiplier_ad_reward_background_png);
        this.reward = value instanceof Texture ? new Sprite(value) : new Text(value, new MultiplierTextStyle());
        this.progressCircle = new Sprite(commonResources.assets_common_multiplier_ad_timer_bar_png);
        this.button = new UIButtonBase({ defaultTexture: commonResources.assets_common_multiplier_ad_ad_button_png });

        this.progressMask = new Graphics();
        this.progressCircle.mask = this.progressMask;

        this.timerBackground.position.set(-63, -44);
        this.rewardBackground.position.set(13, 13);
        this.progressCircle.position.set((this.timerBackground.width - this.progressCircle.width) * 0.5, (this.timerBackground.height - this.progressCircle.height) * 0.5);
        this.progressMask.position.copyFrom(this.progressCircle.position);
        this.reward.position.set((this.rewardBackground.width - this.reward.width) * 0.5, (this.rewardBackground.height - this.reward.height) * 0.5);
        this.button.position.set(98, -52);

        this.rewardBackground.addChild(this.reward);
        this.timerBackground.addChild(this.rewardBackground, this.progressCircle, this.progressMask);
        this.addChild(this.timerBackground, this.button);
        this.visible = initialVisible;
        this.timeout = new Event();
        this.onProgress();
    }

    public resetTimer(): void {
        this.boxMultiplierTimeout = 0;
        this.visible = false;
        Ticker.shared.remove(this.onTick, this);
    }

    public startTimer(): void {
        this.boxMultiplierTimeout = this.timeoutMS;
        this.visible = true;
        this.button.enabled = true;
        this.button.defaultView.tint = "#ffffffff";
        Ticker.shared.add(this.onTick, this);
    }

    public disableTimer(): void {
        this.boxMultiplierTimeout = 0;
        this.button.enabled = false;
        Ticker.shared.remove(this.onTick, this);
    }

    private onProgress(): void {
        const radius = this.progressCircle.width * 0.5;
        this.progressMask.clear();
        this.progressMask.beginFill(0xffffff);
        if ((1 - this.progressValue) == 0) {
            this.progressMask.drawCircle(radius, radius, radius);
            this.progressMask.endFill();
            return;
        }
        this.progressMask.moveTo(radius, radius);
        this.progressMask.arc(
            radius,
            radius,
            radius,
            -Math.PI / 2,
            -Math.PI / 2 + Math.PI * 2 * (1 - this.progressValue),
            true
        );
        this.progressMask.lineTo(radius, radius);
        this.progressMask.endFill();
    }

    private onTick(): void {
        if (this.cancelled || this.destroyed) {
            Ticker.shared.remove(this.onTick, this);
            return;
        }
        this.boxMultiplierTimeout = Math.max(this.boxMultiplierTimeout - Ticker.shared.deltaMS, 0);
        this.value = this.boxMultiplierTimeout / this.timeoutMS;
        if (this.boxMultiplierTimeout === 0) {
            Ticker.shared.remove(this.onTick, this);
            this.timeout.emit();
        }
    }

    public cancelAnimations(): void {
        this.cancelled = true;
        this.boxMultiplierTimeout = 0;
        this.visible = false;
        Ticker.shared.remove(this.onTick, this);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.cancelAnimations();
        super.destroy(options);
    }
}

class MultiplierTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 40,
            fill: "#FFFFFF",
            fontFamily: GameFont.Default,
            dropShadow: true,
            dropShadowColor: "#7500d4",
            dropShadowBlur: 0.5,
            dropShadowDistance: 4,
            dropShadowAngle: 1.77
        });
    }
}
